import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
    Paper,
    Typography,
    Button,
    TextField,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Chip,
    makeStyles
} from "@material-ui/core";
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    AttachFile as AttachFileIcon,
    Close as CloseIcon
} from "@material-ui/icons";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SimpleHtmlEditor from "../../components/SimpleHtmlEditor";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(3),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    form: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(2),
    },
    configSelect: {
        minWidth: 300,
    },
    attachmentsList: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    }
}));

const EmailCompose = () => {
    const classes = useStyles();
    const history = useHistory();
    const location = useLocation();
    const { user } = useContext(AuthContext);

    const [loading, setLoading] = useState(false);
    const [smtpConfigs, setSmtpConfigs] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState("");

    const [to, setTo] = useState("");
    const [cc, setCc] = useState("");
    const [bcc, setBcc] = useState("");
    const [subject, setSubject] = useState("");
    const [body, setBody] = useState("");
    const [attachments, setAttachments] = useState([]);

    // Buscar configurações SMTP
    useEffect(() => {
        const fetchSmtpConfigs = async () => {
            try {
                const { data } = await api.get("/smtp-configs");
                setSmtpConfigs(data);

                if (data.length > 0) {
                    const defaultConfig = data.find(c => c.isDefault) || data[0];
                    setSelectedConfig(defaultConfig.id);
                }
            } catch (err) {
                toastError(err);
            }
        };
        fetchSmtpConfigs();
    }, []);

    // Carregar assinatura ao mudar config (APARECER NO EDITOR)
    useEffect(() => {
        const loadSignature = async () => {
            if (!selectedConfig) return;
            // Se já tem corpo (ex: resposta), não sobrescrever completamente, mas adicionar?
            // Melhor apenas se o corpo estiver vazio ou com assinatura antiga
            // Para simplificar: carrega assinatura se for nova mensagem

            if (location.state?.replyTo || location.state?.forward) return;
            if (body && body.length > 50) return; // Evitar apagar texto que usuário começou a digitar

            try {
                const { data } = await api.get(`/smtp-configs/${selectedConfig}`);
                if (data.signature) {
                    let signatureHtml = "";
                    if (data.signatureType === "image") {
                        // Se começa com http, é URL pública; senão, é base64
                        const src = data.signature.startsWith("http")
                            ? data.signature
                            : (data.signature.startsWith("data:") ? data.signature : `data:image/png;base64,${data.signature}`);
                        signatureHtml = `<br><br><img src="${src}" alt="Assinatura" style="max-height: 100px; width: auto;" />`;
                    } else {
                        signatureHtml = `<br><br>${data.signature.replace(/\n/g, "<br>")}`;
                    }
                    setBody(signatureHtml);
                }
            } catch (err) {
                console.error("Erro assinatura:", err);
            }
        };
        loadSignature();
    }, [selectedConfig]);

    // Preencher campos se for resposta ou encaminhamento
    useEffect(() => {
        if (location.state) {
            const { replyTo, forward, smtpConfigId } = location.state;

            if (smtpConfigId) {
                setSelectedConfig(smtpConfigId);
            }

            if (replyTo) {
                setTo(replyTo.from);
                setSubject(`Re: ${replyTo.subject.replace(/^Re: /, "")}`);
                setBody(`<br><br><br><hr>Em ${new Date(replyTo.date).toLocaleString()}, ${replyTo.from} escreveu:<br>${replyTo.textBody?.replace(/\n/g, "<br>") || ""}`);
            }

            if (forward) {
                setSubject(`Fwd: ${forward.subject.replace(/^Fwd: /, "")}`);
                setBody(`<br><br><br><hr>Mensagem encaminhada<br>De: ${forward.from}<br>Para: ${forward.to}<br>Assunto: ${forward.subject}<br><br>${forward.textBody?.replace(/\n/g, "<br>") || ""}`);
            }
        }
    }, [location.state]);

    const handleBack = () => {
        history.push("/emails");
    };

    const handleFileSelect = (event) => {
        const files = Array.from(event.target.files);
        const maxSize = 25 * 1024 * 1024;
        const invalidFiles = files.filter(f => f.size > maxSize);
        if (invalidFiles.length > 0) {
            toast.error("Alguns arquivos excedem o tamanho máximo de 25MB");
            return;
        }
        setAttachments([...attachments, ...files]);
    };

    const handleRemoveAttachment = (index) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleSend = async () => {
        if (!selectedConfig) {
            toast.warning("Selecione uma conta de e-mail");
            return;
        }
        if (!to) {
            toast.warning("Preencha o destinatário");
            return;
        }
        if (!subject) {
            toast.warning("Preencha o assunto");
            return;
        }

        setLoading(true);
        try {
            const attachmentsData = await Promise.all(
                attachments.map(async (file) => {
                    return new Promise((resolve) => {
                        const reader = new FileReader();
                        reader.onload = () => {
                            resolve({
                                filename: file.name,
                                content: reader.result.split(',')[1],
                                contentType: file.type || 'application/octet-stream'
                            });
                        };
                        reader.readAsDataURL(file);
                    });
                })
            );

            // NÃO adicionamos assinatura aqui pois ela já está no 'body' (html) do editor
            await api.post("/emails/send", {
                smtpConfigId: selectedConfig,
                to,
                cc: cc || undefined,
                bcc: bcc || undefined,
                subject,
                text: body.replace(/<[^>]*>/g, ''),
                html: body,
                attachments: attachmentsData.length > 0 ? attachmentsData : undefined
            });

            toast.success("E-mail enviado com sucesso!");
            history.push("/emails");
        } catch (err) {
            toastError(err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <MainContainer>
            <MainHeader>
                <Title>Novo E-mail</Title>
                <div>
                    <IconButton onClick={handleBack} disabled={loading}>
                        <CloseIcon />
                    </IconButton>
                </div>
            </MainHeader>
            <Paper className={classes.mainPaper} variant="outlined">
                <div className={classes.form}>
                    <FormControl variant="outlined" className={classes.configSelect}>
                        <InputLabel>De (Conta)</InputLabel>
                        <Select
                            value={selectedConfig}
                            onChange={(e) => setSelectedConfig(e.target.value)}
                            label="De (Conta)"
                            disabled={loading}
                        >
                            {smtpConfigs.map((config) => (
                                <MenuItem key={config.id} value={config.id}>
                                    {config.name} &lt;{config.email}&gt;
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Para"
                        variant="outlined"
                        fullWidth
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        placeholder="email@exemplo.com"
                        disabled={loading}
                        required
                    />

                    <div style={{ display: "flex", gap: "16px" }}>
                        <TextField
                            label="Cc"
                            variant="outlined"
                            fullWidth
                            value={cc}
                            onChange={(e) => setCc(e.target.value)}
                            disabled={loading}
                        />
                        <TextField
                            label="Cco"
                            variant="outlined"
                            fullWidth
                            value={bcc}
                            onChange={(e) => setBcc(e.target.value)}
                            disabled={loading}
                        />
                    </div>

                    <TextField
                        label="Assunto"
                        variant="outlined"
                        fullWidth
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        disabled={loading}
                        required
                    />

                    <div style={{ marginTop: 16 }}>
                        <SimpleHtmlEditor
                            value={body}
                            onChange={setBody}
                            disabled={loading}
                        />
                    </div>

                    <div>
                        <input
                            accept="*/*"
                            style={{ display: "none" }}
                            id="attachment-file"
                            multiple
                            type="file"
                            onChange={handleFileSelect}
                            disabled={loading}
                        />
                        <label htmlFor="attachment-file">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<AttachFileIcon />}
                                disabled={loading}
                            >
                                Anexar Arquivos
                            </Button>
                        </label>

                        <div className={classes.attachmentsList}>
                            {attachments.map((file, index) => (
                                <Chip
                                    key={index}
                                    label={`${file.name} (${(file.size / 1024).toFixed(1)} KB)`}
                                    onDelete={() => handleRemoveAttachment(index)}
                                    color="default"
                                    disabled={loading}
                                />
                            ))}
                        </div>
                    </div>

                    <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}>
                        <Button
                            onClick={handleBack}
                            disabled={loading}
                            style={{ marginRight: 16 }}
                        >
                            Cancelar
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <SendIcon />}
                            onClick={handleSend}
                            disabled={loading}
                        >
                            {loading ? "Enviando..." : "Enviar"}
                        </Button>
                    </div>
                </div>
            </Paper>
        </MainContainer>
    );
};

export default EmailCompose;
